
import React from "react";
import { uniq } from "lodash";
import * as zenscroll from "zenscroll";
import * as DateUtil from "@travellocal/utils/dateHelpers";
import {
  ItineraryMeta,
  ItinerarySummaryInfo,
  ItineraryServices,
  ItineraryPricing,
  ItinerarySection,
  Icomoon,
  Container,
  LinkButton,
  ActionBar,
  Icon,
  usePageScrollPosition,
} from "@travellocal/ui";
import { isEnquiryBooked } from "../utils";
import { ItinerarySummaryView } from "../../../../../shared/itinerarySummaryView";
import { subtractDays } from "@travellocal/utils/dateHelpers";
import * as WebRoutes from "@travellocal/utils/webRoutes";
import { add } from "../../../../../bothends/priceHelpers";
import { ItineraryNotesContent } from "./itineraryNotes";
import { MapWrapper } from "../../../../../shared/organisms/mapboxMap";
import { arrayToSentence } from "../../../../../shared/helpers/stringHelpers";
import { NewItineraryMessage } from "../nextActions/newItineraryMessage";
import { GeneratePdfButton } from "../../../../../shared/components/generatePdfButton";

import "./newItinerary.scss";

interface Props {
  itinerary: IEnquiryItinerarySummary;
  enquiry: IEnquiryOutput;
  payments: IPaymentsOutput;
  lto: ILto;
}

const getTotalPaid = (currency: ICurrency, payments: IPaymentsOutput) => {
  const zero: IPriceInCurrency = { currency, price: 0 };
  if (!payments?.payments) {
    return zero;
  }

  return payments.payments.reduce((total, payment) => add(total, payment.charge), zero);
}

const jumpToSection = (classnames: string) => {
  const element = document.getElementsByClassName(classnames)[0] as HTMLElement;
  zenscroll.to(element);
};

export const NewItinerary: React.FC<Props> = ({
  children,
  itinerary,
  enquiry,
  payments,
  lto,
}) => {

  const isCurrentItinerary = itinerary.quoteId === enquiry.currentQuoteId;
  const isItineraryBookable = isCurrentItinerary && !isEnquiryBooked(enquiry);
  const totalPaid = getTotalPaid(enquiry.currency, payments);
  const locations = itinerary.geoLocations.length > 0
    ? itinerary.geoLocations
    : [itinerary.primaryLocation.geoLocation];
  const accomNames = uniq(itinerary.days.map(x => x.dayAccommodation).filter(x => x != null).map(x => x.name));


  const scrollPosition = usePageScrollPosition();
  const showActionBar = scrollPosition.isAtTop || scrollPosition.delta < 0;

  return (
    <div className="customer-itinerary">
      <Container>
        {children} {/* Gallery images are shown here */}

        <div className="level customer-itinerary__head">
          <div className="level-left">
            <ItineraryMeta
              isCurrentItinerary={isCurrentItinerary}
              sentTime={DateUtil.fromNow(itinerary.ltoMessage.sentTime)}
              author={itinerary.ltoMessage.fromUser.firstName}
              versionNumber={itinerary.version}
            />
          </div>
          <div className="level-right is-hidden-mobile">
            {isItineraryBookable && (
              <LinkButton
                to={WebRoutes.customerEnquiryMakePayment(enquiry.id)}
                icomoon={Icomoon.power2}
                iconAlign="right"
                className="is-large"
              >
                Book now
              </LinkButton>
            )}
            {(!isCurrentItinerary) && (
              <LinkButton
                to={WebRoutes.customerEnquiryItinerary(enquiry.id)}
                className="is-large"
              >
                View current itinerary
              </LinkButton>
            )}
          </div>
        </div>

        <ItinerarySummaryInfo
          numberOfPeople={itinerary.numberOfPeople}
          startDate={itinerary.startDate}
          numberOfDays={itinerary.numberOfDays}
          totalPrice={itinerary.totalPrice}
          onClickIncludedServices={() => jumpToSection("itinerary-section itinerary-services")}
          onClickPricingDetails={() => jumpToSection("itinerary-section itinerary-pricing")}
        />

        <div className="customer-itinerary__message">
          <NewItineraryMessage enquiry={enquiry} message={itinerary.ltoMessage} lto={lto} />
        </div>

        <ItinerarySummaryView itinerary={itinerary} lto={lto} />

        <GeneratePdfButton
          itineraryId={itinerary.id}
          className="is-large customer-itinerary__pdf inverted"
        />

        <div className="is-clearfix" />

        <hr />

        <ItineraryServices
          includedServices={itinerary.includedServices}
          excludedServices={itinerary.includedServices}
        />

        <hr />

        <ItineraryPricing
          enquiryId={enquiry.id}
          isBookableItinerary={isItineraryBookable}
          lto={lto}
          totalPrice={itinerary.totalPrice}
          depositPrice={itinerary.depositPrice}
          prepayment={itinerary.prepayment}
          balanceDueDate={itinerary.startDate
            ? subtractDays(itinerary.startDate, itinerary.paymentTerms.balanceDueDays)
            : null}
          lineItems={itinerary.lineItems}
          totalPaid={totalPaid}
        />

        <hr />

        <ItinerarySection
          title="Important notes"
          icomoon={Icomoon.info}
        >
          <ItineraryNotesContent
            enquiry={enquiry}
            itinerary={itinerary}
            lto={lto}
          />
        </ItinerarySection>

        <hr />

        <ItinerarySection
          title="Itinerary map"
          icomoon={Icomoon.location}
        >
          {accomNames.length &&
            <p>Accommodation at {arrayToSentence(accomNames)}</p>
          }
          <MapWrapper height={500} markerLocation={locations} />
        </ItinerarySection>
      </Container>

      {(isItineraryBookable || !isCurrentItinerary) && (
        <ActionBar
          isOpen={showActionBar}
          className="is-hidden-tablet"
          contentClassName="customer-itinerary__actions"
          ariaLabel="Things you can do with this itinerary"
        >
          {isItineraryBookable && (
            <>
              <LinkButton to={WebRoutes.customerEnquiryMakePayment(enquiry.id)}>
                Book now <Icon icomoon={Icomoon.power2} />
              </LinkButton>
              <LinkButton className="inverted" to={WebRoutes.customerEnquiryMessaging(enquiry.id)}>
                Change
              </LinkButton>
            </>
          )}

          {!isCurrentItinerary && (
            <LinkButton to={WebRoutes.customerEnquiryItinerary(enquiry.id)}>
              View current itinerary
            </LinkButton>
          )}
        </ActionBar>
      )}
    </div>
  );
}
